import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { registerStart } from '../../store/slices/authSlice';
import { useTranslation } from '../../utils/translations';
import { isValidInstagram } from '../../utils/instagramHelpers';
import { uploadAPI } from '../../services/api';
import { createCenterCropPreview } from '../../utils/agentPhotoCrop';
import { AGENT_PHOTO_UPLOAD } from '../../constants/agentPhotoSpecs';
import { generateUsernameFromName } from '../../utils/agentHelpers';
import { ButtonLoader } from '../loading';
import '../../pages/AdminRegisterPage.css';

const AgentRegisterForm = ({ onSuccess, onCancel, showFooter = true, showHeader = true }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loading, error } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    instagram: '',
    email: '',
    mobile: '',
    city: 'Tirana',
    title: 'Real Estate Agent',
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [registrationSubmitted, setRegistrationSubmitted] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [usernameManuallyEdited, setUsernameManuallyEdited] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'username') {
      setUsernameManuallyEdited(true);
      setFormData((prev) => ({ ...prev, username: value }));
    } else if (name === 'name' && !usernameManuallyEdited) {
      setFormData((prev) => ({
        ...prev,
        name: value,
        username: generateUsernameFromName(value),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.instagram.trim()) {
      errors.instagram = 'Instagram is required';
    } else if (!isValidInstagram(formData.instagram)) {
      errors.instagram = 'Please enter a valid Instagram username or URL';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    }

    if (!photoFile) {
      errors.photo = t('admin.register.photoRequired') || 'Agent photo is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      setPhotoFile(null);
      setPhotoPreview('');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setValidationErrors((prev) => ({
        ...prev,
        photo: t('admin.register.photoInvalid') || 'Please upload an image file',
      }));
      return;
    }

    if (file.size > AGENT_PHOTO_UPLOAD.maxFileSizeMb * 1024 * 1024) {
      setValidationErrors((prev) => ({
        ...prev,
        photo: t('admin.register.photoTooLarge') || `Photo must be under ${AGENT_PHOTO_UPLOAD.maxFileSizeMb}MB`,
      }));
      return;
    }

    try {
      const preview = await createCenterCropPreview(file, 200);
      setPhotoFile(file);
      setPhotoPreview(preview);
      setValidationErrors((prev) => ({ ...prev, photo: '' }));
    } catch (photoError) {
      setValidationErrors((prev) => ({
        ...prev,
        photo: t('admin.register.photoInvalid') || 'Please upload an image file',
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setUploadingPhoto(true);

    try {
      const uploadResult = await uploadAPI.uploadAgentPhoto(photoFile);
      setRegistrationSubmitted(true);
      dispatch(registerStart({
        username: formData.username,
        password: formData.password,
        name: formData.name,
        instagram: formData.instagram,
        email: formData.email,
        mobile: formData.mobile,
        city: formData.city,
        title: formData.title,
        image: uploadResult.url,
      }));
    } catch (uploadError) {
      setRegistrationSubmitted(false);
      setValidationErrors((prev) => ({
        ...prev,
        photo: uploadError.message || t('admin.register.photoUploadFailed') || 'Failed to upload photo',
      }));
    } finally {
      setUploadingPhoto(false);
    }
  };

  useEffect(() => {
    if (registrationSubmitted && !loading && !error) {
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/admin/dashboard');
      }
    }
  }, [registrationSubmitted, loading, error, navigate, onSuccess]);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate('/admin/dashboard');
  };

  return (
    <>
      {showHeader && (
        <div className="admin-register-header">
          <h2>{t('admin.register.title') || 'Add New Agent'}</h2>
          <p>{t('admin.register.adminOnly') || 'Only logged-in admins can create new agent accounts.'}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-register-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-section">
          <h3 className="form-section-title">{t('accountInfo') || 'Account Information'}</h3>

          <div className="form-group">
            <label htmlFor="username">{t('username') || 'Username'} *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder={t('usernamePlaceholder') || 'Choose a username'}
              required
              autoComplete="username"
              className={validationErrors.username ? 'error' : ''}
            />
            {validationErrors.username && (
              <span className="field-error">{validationErrors.username}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('password') || 'Password'} *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('passwordPlaceholder') || 'At least 6 characters'}
              required
              autoComplete="new-password"
              className={validationErrors.password ? 'error' : ''}
            />
            {validationErrors.password && (
              <span className="field-error">{validationErrors.password}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t('confirmPassword') || 'Confirm Password'} *</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('admin.register.confirmPasswordPlaceholder') || 'Re-enter password'}
              required
              autoComplete="new-password"
              className={validationErrors.confirmPassword ? 'error' : ''}
            />
            {validationErrors.confirmPassword && (
              <span className="field-error">{validationErrors.confirmPassword}</span>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3 className="form-section-title">{t('admin.register.agentInfo') || 'Agent Information'}</h3>

          <div className="form-group">
            <label htmlFor="name">{t('admin.register.name') || 'Full Name'} *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('admin.register.namePlaceholder') || 'Agent full name'}
              required
              className={validationErrors.name ? 'error' : ''}
            />
            {validationErrors.name && (
              <span className="field-error">{validationErrors.name}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="photo">{t('admin.register.photo') || 'Agent Photo'} *</label>
            <div className="agent-photo-upload">
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt={t('admin.register.photoPreview') || 'Photo preview'}
                  className="agent-photo-preview"
                />
              ) : (
                <div className="agent-photo-placeholder">
                  {t('admin.register.photoPlaceholder') || 'Upload a photo'}
                </div>
              )}
              <input
                type="file"
                id="photo"
                name="photo"
                accept="image/jpeg,image/png,image/jpg,image/webp"
                onChange={handlePhotoChange}
                className={validationErrors.photo ? 'error' : ''}
              />
            </div>
            <span className="field-hint">
              {t('admin.register.photoHint') || `Square crop from center, saved at ${AGENT_PHOTO_UPLOAD.width}×${AGENT_PHOTO_UPLOAD.height}px`}
            </span>
            {validationErrors.photo && (
              <span className="field-error">{validationErrors.photo}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="instagram">{t('admin.register.instagram') || 'Instagram'} *</label>
            <input
              type="text"
              id="instagram"
              name="instagram"
              value={formData.instagram}
              onChange={handleChange}
              placeholder={t('admin.register.instagramPlaceholder') || '@premiumhomes'}
              required
              className={validationErrors.instagram ? 'error' : ''}
            />
            {validationErrors.instagram && (
              <span className="field-error">{validationErrors.instagram}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('admin.register.email') || 'Email'} *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('admin.register.emailPlaceholder') || 'agent@premiumhomes.al'}
              required
              autoComplete="email"
              className={validationErrors.email ? 'error' : ''}
            />
            {validationErrors.email && (
              <span className="field-error">{validationErrors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="mobile">{t('admin.register.mobile') || 'Mobile Number'} *</label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              placeholder={t('admin.register.mobilePlaceholder') || '+355 69 123 4567'}
              required
              autoComplete="tel"
              className={validationErrors.mobile ? 'error' : ''}
            />
            {validationErrors.mobile && (
              <span className="field-error">{validationErrors.mobile}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">{t('admin.register.city') || 'City'} *</label>
              <select
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
              >
                <option value="Tirana">Tirana</option>
                <option value="Durrës">Durrës</option>
                <option value="Vlorë">Vlorë</option>
                <option value="Sarandë">Sarandë</option>
                <option value="Shkodër">Shkodër</option>
                <option value="Korçë">Korçë</option>
                <option value="Elbasan">Elbasan</option>
                <option value="Fier">Fier</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="title">{t('admin.register.type') || 'Type'} *</label>
              <select
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              >
                <option value="Real Estate Agent">Real Estate Agent</option>
                <option value="Broker">Broker</option>
                <option value="Office Manager">Office Manager</option>
                <option value="Estate Office">Estate Office</option>
              </select>
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="admin-register-button"
          disabled={loading || uploadingPhoto}
        >
          {loading || uploadingPhoto
            ? <ButtonLoader />
            : (t('admin.register.registerButton') || 'Create Agent Account')}
        </button>
      </form>

      {showFooter && (
        <div className="admin-register-footer">
          <button type="button" onClick={handleCancel} className="back-to-home">
            ← {t('admin.register.backToDashboard') || 'Back to Dashboard'}
          </button>
        </div>
      )}
    </>
  );
};

export default AgentRegisterForm;
